import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { getAllData, postUsersData } from '../services/ApiCallingUserServices';
import { loaderActions } from '../_actions/loader.actions';
import { agentActions } from '../_actions/agent.action';
import { useSelector } from 'react-redux';
import { AgentStatus } from '../_constants/agent.constant';
import { ApiNotRespondMessage } from '../components/commonClass/CommonValidation';

interface StatusActionModalProps {
    show: boolean;
    onHide: () => void;
    actionType: 'Approve' | 'Reject' | 'View';
    viewData?: {
        Remark?: string;
        RejectionReason?: string;
    };
    onSuccess?: () => void;
    TravelAgentId?: number;
    CorporateAgentId?: number;
}

const StatusActionModal: React.FC<StatusActionModalProps> = ({
    show,
    onHide,
    actionType,
    viewData,
    onSuccess,
    TravelAgentId = 0,
    CorporateAgentId = 0,
}) => {
    const dispatch = useDispatch();
    const { data: agentData } = useSelector((store: any) => store.agentOptions);
    const reasons = agentData?.reasonOptions || [];
    const [selectedReason, setSelectedReason] = useState<number>(0);
    const [remark, setRemark] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const statusOptions = agentData?.statusOptions || [];

    const { approvedStatusId, rejectedStatusId } = useMemo(() => {
        return {
            approvedStatusId: statusOptions.find((opt: any) => opt.StatusName === 'Approved')?.StatusId || AgentStatus.Approved,
            rejectedStatusId: statusOptions.find((opt: any) => opt.StatusName === 'Rejected')?.StatusId || AgentStatus.Rejected
        };
    }, [statusOptions]);

    useEffect(() => {
        if (show && actionType === 'Reject') {
            (dispatch as any)(agentActions.getAgentOptions());
        }
        if (show) {
            setRemark('');
            setSelectedReason(0);
            setErrorMsg('');
        }
    }, [show, actionType, dispatch]);

    const handleSave = async () => {
        if (actionType === 'Reject' && selectedReason === 0) {
            setErrorMsg('Please select a reason');
            return;
        }

        const payload = {
            CorporateAgentId,
            TravelAgentId,
            StatusId: actionType === 'Approve' ? approvedStatusId : rejectedStatusId,
            RejectionReasonId: actionType === 'Reject' ? selectedReason : 0,
            Remark: actionType === 'Approve' ? remark : ''
        };

        dispatch(loaderActions.start());
        try {
            const response = await postUsersData('/midoffice/v1/updateagentstatus', payload);
            if (response?.status) {
                dispatch({ type: 'ALERTING', message: response?.message || 'Status updated successfully' });
                if (onSuccess) onSuccess();
                onHide();
            } else {
                dispatch({ type: 'ALERTING', message: response?.message || ApiNotRespondMessage });
            }
        } catch (error) {
            dispatch({ type: 'ALERTING', message: ApiNotRespondMessage });
        } finally {
            dispatch(loaderActions.end());
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {actionType === 'Approve' ? 'Approve Application' : actionType === 'Reject' ? 'Reject Application' : 'Reason / Remark Detail'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/*this is action form*/}
                {actionType === 'Reject' && (
                    <div className="form-group">
                        <label className="mb-2">Select Reason <span className="text-danger">*</span></label>
                        <select
                            className={`form-control form-select ${errorMsg ? 'is-invalid' : ''}`}
                            value={selectedReason}
                            onChange={(e) => {
                                setSelectedReason(Number(e.target.value));
                                setErrorMsg('');
                            }}
                        >
                            <option value={0}>Select a reason</option>
                            {reasons.map((reason: any, index: number) => (
                                <option key={index} value={reason?.AgentReasonId}>
                                    {reason?.ReasonText}
                                </option>
                            ))}
                        </select>
                        {errorMsg && <div className="text-danger mt-1 fs_14">{errorMsg}</div>}
                    </div>
                )}

                {/*this is Action remark/reason review-section*/}
                {actionType === 'Approve' && (
                    <div className="form-group">
                        <label className="mb-2">Remark</label>
                        <textarea
                            className="form-control"
                            rows={4}
                            maxLength={150}
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            placeholder="Enter remark here..."
                        />
                        <div className="text-end text-muted fs_12 mt-1 text-wrap text-break">{remark.length}/150</div>
                    </div>
                )}

                {actionType === 'View' && (
                    <div>
                        {viewData?.RejectionReason && (
                            <div className="mb-3">
                                <strong>Rejection Reason:</strong>
                                <p className="mt-1 text-wrap text-break">{viewData.RejectionReason}</p>
                            </div>
                        )}
                        {viewData?.Remark && (
                            <div>
                                <strong>Remark:</strong>
                                <p className="mt-1 text-wrap text-break">{viewData.Remark}</p>
                            </div>
                        )}
                        {!viewData?.RejectionReason && !viewData?.Remark && (
                            <p>No reason or remark found.</p>
                        )}
                    </div>
                )}
            </Modal.Body>
            {actionType !== 'View' && (
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onHide}>Close</button>
                    <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default StatusActionModal;
